const BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api/v1";

const TOKEN_KEY = "cysol_access_token";
const REFRESH_KEY = "cysol_refresh_token";

export function getAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

export function setTokens(access: string, refresh: string) {
  localStorage.setItem(TOKEN_KEY, access);
  localStorage.setItem(REFRESH_KEY, refresh);
}

export function clearTokens() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

export function isAuthenticated(): boolean {
  return !!getAccessToken();
}

export interface ApiError {
  status: number;
  message: string;
  detail?: unknown;
}

/** Plain object thrown by handleResponse — not instanceof Error. */
export function apiErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error) return err.message;
  if (
    typeof err === "object" &&
    err !== null &&
    "message" in err &&
    typeof (err as ApiError).message === "string"
  ) {
    return (err as ApiError).message;
  }
  return fallback;
}

class ApiClient {
  private baseUrl: string;
  private refreshPromise: Promise<boolean> | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private headers(extra?: Record<string, string>, authToken?: string): Record<string, string> {
    const h: Record<string, string> = { ...extra };
    const token = authToken ?? getAccessToken();
    if (token) h["Authorization"] = `Bearer ${token}`;
    return h;
  }

  private async handleResponse<T>(res: Response): Promise<T> {
    if (res.status === 204) return undefined as T;

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      let message: string = res.statusText;
      if (typeof body.detail === 'string') message = body.detail;
      else if (Array.isArray(body.detail) && body.detail[0]?.msg) message = body.detail[0].msg;
      else if (body.detail && typeof body.detail === 'object' && !Array.isArray(body.detail)) {
        const d = body.detail as Record<string, unknown>;
        if (typeof d.message === 'string') message = d.message;
      } else if (body.detail) message = String(body.detail);
      const err: ApiError = {
        status: res.status,
        message,
        detail: body,
      };
      throw err;
    }

    const contentType = res.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      return res.json();
    }
    return res.text() as unknown as T;
  }

  private async refreshAccessToken(): Promise<boolean> {
    const refresh = getRefreshToken();
    if (!refresh) return false;

    try {
      const res = await fetch(`${this.baseUrl}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refresh }),
      });
      if (!res.ok) return false;
      const data = await res.json();
      setTokens(data.access_token, data.refresh_token);
      return true;
    } catch {
      return false;
    }
  }

  private async requestWithRetry<T>(
    method: string,
    path: string,
    opts?: { body?: unknown; headers?: Record<string, string>; rawBody?: BodyInit; authToken?: string }
  ): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    const isFormData = opts?.rawBody instanceof FormData;

    const buildInit = (): RequestInit => {
      const h = this.headers(opts?.headers, opts?.authToken);
      if (!isFormData && opts?.body) h["Content-Type"] = "application/json";

      return {
        method,
        headers: h,
        body: isFormData
          ? opts!.rawBody
          : opts?.body
            ? JSON.stringify(opts.body)
            : undefined,
      };
    };

    let res = await fetch(url, buildInit());

    if (res.status === 401 && getRefreshToken()) {
      if (!this.refreshPromise) {
        this.refreshPromise = this.refreshAccessToken().finally(() => {
          this.refreshPromise = null;
        });
      }
      const refreshed = await this.refreshPromise;
      if (refreshed) {
        res = await fetch(url, buildInit());
      } else {
        clearTokens();
        // Misma base que Vite / BrowserRouter (subcarpeta o raíz).
        window.location.href = `${import.meta.env.BASE_URL}login`.replace(/\/{2,}/g, '/');
        throw { status: 401, message: "Session expired" } as ApiError;
      }
    }

    return this.handleResponse<T>(res);
  }

  get<T>(path: string, authToken?: string) {
    return this.requestWithRetry<T>("GET", path, { authToken });
  }

  post<T>(path: string, body?: unknown, authToken?: string) {
    return this.requestWithRetry<T>("POST", path, { body, authToken });
  }

  patch<T>(path: string, body?: unknown, authToken?: string) {
    return this.requestWithRetry<T>("PATCH", path, { body, authToken });
  }

  put<T>(path: string, body?: unknown, authToken?: string) {
    return this.requestWithRetry<T>("PUT", path, { body, authToken });
  }

  delete<T = void>(path: string, authToken?: string) {
    return this.requestWithRetry<T>("DELETE", path, { authToken });
  }

  upload<T>(path: string, file: File, fieldName = "file", authToken?: string) {
    const fd = new FormData();
    fd.append(fieldName, file);
    return this.requestWithRetry<T>("POST", path, { rawBody: fd, authToken });
  }
}

export const api = new ApiClient(BASE_URL);

export interface Page<T> {
  items: T[];
  total: number;
  skip: number;
  limit: number;
}

export function buildQuery(params: Record<string, unknown> | object): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params as Record<string, unknown>)) {
    if (v !== undefined && v !== null && v !== "") {
      parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
    }
  }
  return parts.length ? `?${parts.join("&")}` : "";
}
